'use client';
import { useState } from 'react';
import { createClient } from '@/lib/supabase/client';

export default function ShootReleaseDownloadForm() {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!email || !/^[^@]+@[^@]+\.[^@]+$/.test(email)) {
      setError('Please enter a valid email address.');
      return;
    }
    setLoading(true);
    setError('');
    try {
      const supabase = createClient();
      const { error: dbError } = await supabase.from('diy_downloads').insert({
        email: email.toLowerCase().trim(),
        resource_name: 'Photo & Video Shoot Release',
      });
      if (dbError) throw dbError;
      setSuccess(true);
    } catch (err: unknown) {
      console.error('Shoot release download gate error:', err);
      setError('Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="border border-[#2a2a2a] bg-[#080808] p-8">
      {!success ? (
        <>
          <div className="mb-8">
            <p className="text-[10px] uppercase tracking-[0.3em] text-primary mb-2 font-semibold">Free Download</p>
            <h2 className="text-xl font-bold mb-3">Get the Free Shoot Release</h2>
            <p className="text-[#888] text-sm leading-relaxed">
              Enter your email and we&apos;ll unlock the template instantly. No spam — just the document and occasional marketing resources from Best Odds.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div>
              <label className="block text-xs text-[#888] mb-2 uppercase tracking-wider">
                Your Email Address
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                className="w-full bg-[#111] border border-[#2a2a2a] text-white placeholder-[#444] px-4 py-3 text-sm focus:outline-none focus:border-primary transition-colors"
                required
                autoFocus
              />
              {error && <p className="text-red-400 text-xs mt-2">{error}</p>}
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-primary hover:bg-primary/90 text-white font-semibold py-3.5 px-6 text-sm uppercase tracking-wider transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                'Unlocking...'
              ) : (
                <>
                  <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                  </svg>
                  Unlock Free Shoot Release
                </>
              )}
            </button>

            <p className="text-[11px] text-[#555] text-center">
              No spam. Unsubscribe anytime.
            </p>
          </form>

          <div className="mt-8 pt-6 border-t border-[#1a1a1a]">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#555] font-semibold mb-4">Also Available Free</p>
            <ul className="space-y-2">
              {[
                { label: 'Free NDA Template', href: '/free-nda' },
                { label: 'Model & Talent Release', href: '/diy' },
                { label: 'Social Media Content Release', href: '/diy' },
                { label: 'IC Agreement Template', href: '/diy' },
              ].map((item) => (
                <li key={item.label}>
                  <a
                    href={item.href}
                    className="text-xs text-[#666] hover:text-primary transition-colors flex items-center gap-2"
                  >
                    <svg className="w-3 h-3 text-primary shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                    {item.label}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </>
      ) : (
        <div className="text-center py-8">
          <div className="w-16 h-16 rounded-full bg-primary/10 border border-primary/30 flex items-center justify-center mx-auto mb-6">
            <svg className="w-8 h-8 text-primary" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <h3 className="text-xl font-bold mb-3">Your release form is ready.</h3>
          <p className="text-[#888] text-sm mb-6 leading-relaxed">
            The Photo &amp; Video Shoot Release is being prepared for you. Check your inbox — a download link will be sent shortly.
          </p>
          <a
            href="/diy"
            className="text-xs uppercase tracking-wider text-primary hover:underline"
          >
            Browse More Free Templates →
          </a>
        </div>
      )}
    </div>
  );
}